import React, { useEffect, useState } from "react"
import { Collapse, Steps } from "antd"
import { UpOutlined } from "@ant-design/icons"
import { CircularProgressbar, buildStyles } from "react-circular-progressbar"
import "react-circular-progressbar/dist/styles.css"
import ImplementationTrendChart from "./ImplementationRubric/ImplementationTrendChart"
import Pyramid from "./ProgramMatrix/Pyramid"
import Mtss from "./ProgramMatrix/Mtss"
import ProgramMatrixSELQuality from "./ProgramMatrix/SELQuality"
import SchoolProgressReportButton from "./SchoolProgressReportButton"
import Loader from "./CommonComponents/Loader"
import commonStrings from "../commonStrings"

const { Panel } = Collapse
const { Step } = Steps
const customDot = () => <div className="custom-dot " />

const COLORS = {
  implementation: "#E7A922",
  sustainability: "#33485D",
  programMatrix: "#0CA4A5",
  overall: "#A6192E",
}

// Matches SustainabilityRubric/Components/SustainabilityObjects.jsx's sustainabilityMainHeaders
// (title/color/description) and NestedCollapsibleforReport.jsx's filterObjectsByTitle (which
// area names belong to which category) — both the banners and the area order/colors on the real
// Sustainability Rubric page, since the API's area hashes carry no category field of their own.
const CATEGORIES = [
  {
    title: "Commitment",
    color: "#A6192E",
    description:
      "The demonstrated willingness of the school/district to participate in activities that create a continued capacity for delivery of program concepts and tenants.",
    areas: [
      "Physical Visibility",
      "Program Review",
      "Communication",
      "Leadership",
      "School Norms",
      "Data",
    ],
  },
  {
    title: "Integration",
    color: "#E7A922",
    description:
      "The steps the school/district has taken to incorporate program delivery into relevant ongoing and future activities.",
    areas: ["Coordination", "Range"],
  },
  {
    title: "Innovation",
    color: "#0CA4A5",
    description:
      "The steps the school or district has taken to adapt the program in order to meet the needs of the stakeholders (in accordance with core program tenets).",
    areas: ["Program Fit/Reinvention"],
  },
]

const ProgressRing = ({
  percent, radius, strokeWidth, label,
}) => {
  const circumference = 2 * Math.PI * radius
  const safePercent = Math.max(0, Math.min(100, percent || 0))
  const dashoffset = circumference * (1 - safePercent / 100)
  const size = (radius + strokeWidth) * 2

  return (
    <div className="spr-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#EDEEF0"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={COLORS.overall}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashoffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="spr-ring__overlay">
        <div
          className="spr-ring__value"
          style={{ fontSize: radius > 45 ? 28 : 17 }}
        >
          {percent === null || percent === undefined ? "—" : `${percent}%`}
        </div>
        {label && <div className="spr-ring__label">{label}</div>}
      </div>
    </div>
  )
}

const SectionHeader = ({
  icon,
  iconTint,
  title,
  badgeColor,
  badgeTint,
  badge,
  meta,
}) => (
  <div className="spr-section-header">
    <div className="spr-section-header__icon" style={{ background: iconTint }}>
      {icon}
    </div>
    <div className="spr-section-header__title">{title}</div>
    {meta && <div className="spr-section-header__meta">{meta}</div>}
    {badge && (
      <div
        className="spr-section-header__badge"
        style={{ color: badgeColor, background: badgeTint }}
      >
        {badge}
      </div>
    )}
  </div>
)

// Matches ImplementationRubric/Report.jsx's own per-stage colors exactly (its `stages` array).
const STAGE_COLORS = {
  "Commitment Stage I": "#A6192E",
  "Commitment Stage II": "#33485D",
  "Implementation Stage I": "#E7A922",
  "Implementation Stage II": "#0CA4A5",
}

const ImplementationDetail = ({ implementation }) => {
  const stages = implementation.report || []

  return (
    <div>
      <div className="spr-chart-wrap">
        <ImplementationTrendChart rubric={implementation} />
      </div>
      {stages.slice(0, 4).map((stage, index) => {
        const stageColor = STAGE_COLORS[stage.label] || COLORS.implementation
        const hasAreas = (stage.areas_of_strength && stage.areas_of_strength.length > 0)
          || (stage.areas_of_need && stage.areas_of_need.length > 0)
        const templateStage = (implementation.template
            && implementation.template.stages
            && implementation.template.stages[index])
          || {}

        return (
          <div key={stage.label} className="spr-stage">
            <div
              className="container paddingHeader spr-stage-banner"
              style={{ backgroundColor: stageColor }}
            >
              <div className="d-flex align-items-center div-collapse">
                <div className="title-collapse">
                  <span>{stage.label}</span>
                </div>
                <div className="subHeading-collapse">
                  <span>
                    <span className="partSubHeading">
                      {templateStage.heading}
                    </span>
                    {" - "}
                    <span>{templateStage.description}</span>
                  </span>
                </div>
                <div className="progress-collapse">
                  {stage.completion < 0 ? (
                    <span className="incomplete-text">
                      {commonStrings.inCompleteText}
                    </span>
                  ) : (
                    <div className="spr-stage__ring-wrap">
                      <CircularProgressbar
                        text={`${stage.completion}%`}
                        value={stage.completion}
                        strokeWidth={15}
                        styles={buildStyles({
                          textColor: "white",
                          pathColor: "white",
                          trailColor: "#ffffff33",
                          textAlign: "center",
                        })}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {hasAreas && (
              <>
                <div className="d-flex justify-content-center mt40">
                  <div className="d-flex blockArea">
                    <div
                      className="areas"
                      style={{
                        borderBottomColor: stageColor,
                        color: stageColor,
                      }}
                    >
                      <span>Areas of strength</span>
                    </div>
                    <div
                      className="spr-divider"
                      style={{ background: stageColor }}
                    />
                    <div
                      className="areas"
                      style={{
                        borderBottomColor: stageColor,
                        color: stageColor,
                      }}
                    >
                      <span>Areas of need</span>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-center">
                  <div className="d-flex blockArea">
                    <div className="areasText">
                      {stage.areas_of_strength
                      && stage.areas_of_strength.length > 0 ? (
                        <ul className="paddingTop40 padding-right">
                          {stage.areas_of_strength.map((area, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={i} dangerouslySetInnerHTML={area} />
                          ))}
                        </ul>
                        ) : (
                          <div className="paddingTop40 padding-right spr-empty-note">
                            None noted for this stage.
                          </div>
                        )}
                    </div>
                    <div
                      className="spr-divider"
                      style={{ background: stageColor }}
                    />
                    <div className="areasText">
                      {stage.areas_of_need && stage.areas_of_need.length > 0 ? (
                        <ul className="paddingTop40">
                          {stage.areas_of_need.map((area, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={i} dangerouslySetInnerHTML={area} />
                          ))}
                        </ul>
                      ) : (
                        <div className="paddingTop40 spr-empty-note">
                          None noted for this stage.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Matches SustainabilityRubric/Report.jsx's own stepper exactly (stepTitles/determineCurrentStep),
// so this reads identically to the real Sustainability Rubric page: the same antd Steps diamond
// track and bordered step-title boxes, styled by the already-global .step-title/.custom-dot/
// .Multi-stepper rules in application.scss.
const RATING_TITLES = [
  "INITIATING",
  "DEVELOPING",
  "PROFICIENT",
  "WELL DEVELOPED",
]
const RATING_STEP_INDEX = {
  initiating: 0,
  developing: 1,
  proficient: 2,
  "well developed": 3,
}

// Matches SustainabilityRubric/Artifacts.jsx: area.evidence, link text is description||filename,
// and previewable files (image/pdf, per EvidenceSerializer#previewable) embed via <object> the
// same way, just in a single column instead of that page's paired-row layout.
const EvidenceList = ({ evidence, color = COLORS.sustainability }) => {
  if (!evidence || evidence.length === 0) {
    return null
  }

  return (
    <div className="spr-evidence">
      <div className="spr-evidence__heading" style={{ color }}>
        {commonStrings.artifacts}
      </div>
      {evidence.map((file) => (
        <div key={file.id} className="spr-evidence__item">
          <div className="spr-evidence__link-row">
            <span style={{ color }}>&bull;</span>
            <a
              href={file.url}
              target="_blank"
              rel="noreferrer"
              className="spr-evidence__link"
              style={{ color }}
            >
              {file.description || file.filename}
            </a>
          </div>
          {file.previewable && (
            <a href={file.url} target="_blank" rel="noreferrer">
              <object
                data={`${file.url}#scrollbar=0&toolbar=0&navpanes=0`}
                type={file.content_type}
                width="220"
                height="160"
                className="spr-evidence__preview"
              />
            </a>
          )}
        </div>
      ))}
    </div>
  )
}

// The Evidence / Areas of strength & need / Artifacts content revealed when an area row (inside
// a category band) is expanded.
const AreaDetail = ({ group, color }) => {
  const explanations = group.explanations || []

  return (
    <div>
      {explanations.length > 0 && (
        <div>
          <div className="spr-area__section-heading" style={{ color }}>
            Evidence
          </div>
          <ul className="spr-area__list">
            {explanations.map((explanation, index) => (
              <li
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className="spr-area__list-item"
                dangerouslySetInnerHTML={explanation}
              />
            ))}
          </ul>
        </div>
      )}

      <div className="d-flex justify-content-center mt40">
        <div className="d-flex blockArea">
          <div className="areas" style={{ borderBottomColor: color, color }}>
            <span>Areas of strength</span>
          </div>
          <div className="spr-divider" style={{ background: color }} />
          <div className="areas" style={{ borderBottomColor: color, color }}>
            <span>Areas of need</span>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-center">
        <div className="d-flex blockArea">
          <div className="areasText">
            {group.areas_of_strength ? (
              <div className="paddingTop40 padding-right spr-area__text">
                {group.areas_of_strength}
              </div>
            ) : (
              <div className="paddingTop40 padding-right spr-empty-note">
                None noted for this stage.
              </div>
            )}
          </div>
          <div className="spr-divider" style={{ background: color }} />
          <div className="areasText">
            {group.opportunities_for_growth ? (
              <div className="paddingTop40 spr-area__text">
                {group.opportunities_for_growth}
              </div>
            ) : (
              <div className="paddingTop40 spr-empty-note">
                None noted for this stage.
              </div>
            )}
          </div>
        </div>
      </div>

      <EvidenceList evidence={group.evidence} color={color} />
    </div>
  )
}

// A category's colored banner + its areas, each collapsible to reveal AreaDetail — matching the
// real Sustainability Rubric page's Commitment/Integration/Innovation bands exactly (order,
// colors, and which areas sit under which band).
const CategorySection = ({ category, groupsByName }) => {
  const areas = category.areas
    .map((name) => groupsByName[name])
    .filter(Boolean)

  if (areas.length === 0) {
    return null
  }

  return (
    <Collapse
      bordered={false}
      className="spr-category"
      expandIconPosition="end"
      expandIcon={({ isActive }) => (
        <UpOutlined
          className="spr-category__expand-icon"
          rotate={isActive ? 0 : 180}
        />
      )}
    >
      <Panel
        key={category.title}
        style={{ background: category.color }}
        header={(
          <div className="spr-category__banner">
            <div className="spr-category__title">{category.title}</div>
            <div className="spr-category__description">
              {category.description}
            </div>
          </div>
        )}
      >
        <Collapse bordered={false} className="spr-category__areas">
          {areas.map((group) => (
            <Panel
              key={group.name}
              header={(
                <div className="spr-area-row">
                  <div
                    className="spr-area-row__name"
                    style={{ color: category.color }}
                  >
                    {group.name}
                  </div>
                  {group.body && (
                    <div
                      className="spr-area-row__body"
                      style={{ color: category.color }}
                    >
                      {group.body.trim()}
                    </div>
                  )}
                  <div
                    className="spr-area-row__badge"
                    style={{
                      color: category.color,
                      border: `2px solid ${category.color}`,
                    }}
                  >
                    {(group.rating || "Ungraded").toUpperCase()}
                  </div>
                </div>
              )}
            >
              <AreaDetail group={group} color={category.color} />
            </Panel>
          ))}
        </Collapse>
      </Panel>
    </Collapse>
  )
}

const SustainabilityDetail = ({ sustainability }) => {
  const currentStep = sustainability.rating
    ? RATING_STEP_INDEX[sustainability.rating.toLowerCase()] || 0
    : null
  const groupsByName = (sustainability.report || []).reduce((acc, group) => {
    acc[group.name] = group
    return acc
  }, {})

  return (
    <div>
      {currentStep !== null && (
        <div className="stepper-container spr-stepper-wrap">
          <div
            className="stepper-titles"
            style={{ display: "flex", alignItems: "baseline" }}
          >
            {RATING_TITLES.map((title, index) => (
              <div
                className={`step-title spr-step-title-inner ${
                  index <= currentStep ? "active" : ""
                }`}
                key={title}
              >
                {title}
              </div>
            ))}
          </div>
          <div className="Multi-stepper">
            <div>
              <Steps current={currentStep} progressDot={customDot}>
                {RATING_TITLES.map((title) => (
                  <div key={title}>
                    <Step />
                  </div>
                ))}
              </Steps>
            </div>
          </div>
        </div>
      )}
      {CATEGORIES.map((category) => (
        <CategorySection
          key={category.title}
          category={category}
          groupsByName={groupsByName}
        />
      ))}
    </div>
  )
}

// Shared by the SEL Scan and DESSA Report bands below — same matching rule
// ProgramMatrixCollapsible.jsx's matchingItems uses, minus the filters this read-only summary
// view doesn't offer.
const matrixItemsForIndicator = (completedItems, key, type) => (completedItems || []).filter(
  (item) => item.adult === (type === "adult")
      && Object.keys(item.sel_standards || {}).includes(key),
)

const matrixItemSummary = (item) => [item.category, item.tier_of_support, item.length_of_time]
  .filter(Boolean)
  .join(", ")

// Same bullet format ProgramMatrixCollapsible.jsx's "all-data" branch uses for a matched
// student item specifically — the only one of the two that appends grade levels.
const studentItemSummary = (item) => {
  const grades = Array.isArray(item.internal_selected_grades)
    ? item.internal_selected_grades
    : []
  const base = matrixItemSummary(item)
  return grades.length
    ? `${base}, Grades: ${grades.map((grade) => grade.grade).join(", ")}`
    : base
}

const DOMAIN_COLORS = ["#A6192E", "#33485D", "#E7A922", "#0CA4A5"]
const DOMAIN_COLOR_RGB = {
  "#A6192E": "166, 25, 46",
  "#33485D": "51, 72, 93",
  "#E7A922": "231, 169, 34",
  "#0CA4A5": "12, 164, 165",
}

// Read-only port of the real SEL Scan / DESSA Report page's domain-band + indicator-row markup
// (ProgramMatrix/ProgramMatrixCollapsible.jsx's "all-data" branch — one Collapse per domain,
// reusing its existing global classes so the colors/typography/tinted-row treatment match
// exactly), minus its filters, PDF download, and scroll-to-top controls.
const SelDomainBands = ({
  title, groups, completedItems, showDescription,
}) => {
  if (!groups || !groups.length) return null

  return (
    <div className="spr-matrix-section">
      {title && <div className="spr-matrix-section__heading">{title}</div>}
      {groups.map((group, index) => {
        const color = DOMAIN_COLORS[index % DOMAIN_COLORS.length]
        const colorRgb = DOMAIN_COLOR_RGB[color]
        const keys = Object.keys(group.standards || {})

        return (
          <div
            className="collapsee"
            key={group.name}
            style={{ padding: "9px 0" }}
          >
            <Collapse
              style={{ background: color, color: "white" }}
              expandIconPosition="end"
              collapsible="header"
              expandIcon={({ isActive }) => (
                <UpOutlined
                  style={{
                    fontSize: "20px",
                    color: "white",
                    cursor: "pointer",
                  }}
                  rotate={isActive ? 0 : 180}
                />
              )}
            >
              <Panel
                key={group.name}
                header={(
                  <div className="new-collapsible-div overall-collapsible">
                    <div className="c-ttile">
                      <div className="new-collapsible-title-SELScan">
                        {group.name}
                      </div>
                    </div>
                    {showDescription && group.description && (
                      <div className="sel-description">{group.description}</div>
                    )}
                  </div>
                )}
              >
                <div className="inner-contaier-body">
                  {keys.map((key) => {
                    const standard = group.standards[key]
                    const studentItems = matrixItemsForIndicator(
                      completedItems,
                      key,
                      "student",
                    )
                    const adultItems = matrixItemsForIndicator(
                      completedItems,
                      key,
                      "adult",
                    )
                    const hasStudentActivity = studentItems.length > 0
                    const hasAdultActivity = adultItems.length > 0
                    const showAdultActivity = adultItems.some(
                      (item) => item.category === "Adult SEL",
                    )

                    return (
                      <div className="sel-inner" key={key}>
                        <div className="students_in_sel">
                          <div
                            className="all-data"
                            style={{
                              fontWeight: hasStudentActivity ? "700" : "400",
                              backgroundColor:
                                hasStudentActivity || hasAdultActivity
                                  ? `rgba(${colorRgb}, 0.1)`
                                  : "transparent",
                            }}
                          >
                            <div className="student-key">
                              <span style={{ color }}>{key}</span>
                            </div>
                            <div className="inside-data">
                              {standard.student_indicator_language && (
                                <div className="student-key-data">
                                  {standard.student_indicator_language}
                                </div>
                              )}
                              {hasStudentActivity && (
                                <div className="competencies">
                                  <ul>
                                    {studentItems.map((item) => (
                                      <li key={item.id}>
                                        {item.activity} (
                                        {studentItemSummary(item)})
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {standard.adult_indicator_language && (
                                <div
                                  className="adults-sel"
                                  style={{
                                    fontWeight: hasAdultActivity
                                      ? "700"
                                      : "400",
                                  }}
                                >
                                  <span>
                                    {standard.adult_indicator_language}
                                  </span>
                                  {showAdultActivity && (
                                    <div className="competencies">
                                      <ul>
                                        {adultItems.map((item) => (
                                          <li key={item.id}>
                                            {item.activity} (
                                            {matrixItemSummary(item)})
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Panel>
            </Collapse>
          </div>
        )
      })}
    </div>
  )
}

// Read-only port of ProgramMatrix/ActivityDetails.jsx's per-activity report cards — same
// classes, same always-expanded stacked-card layout (no accordion), without its filters or PDF
// download. `startCase`/`findLoadIndicatorsByKeys`/`itemStandard`/the overall card markup below
// mirror that file's own helpers of the same purpose, with `selStandards` (this page's
// `programMatrix.sel_standards`) taking the place of its `window.gon.SEL_STANDARDS` global.
const startCase = (str) => str.replace(
  /\w\S*/g,
  (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
)

const findLoadIndicatorsByKeys = (keys, loadIndicators) => {
  const categories = []

  keys.forEach((key) => {
    Object.keys(loadIndicators || {}).forEach((category) => {
      if (
        (loadIndicators[category] || []).includes(key)
        && !categories.some((cat) => cat.name === category)
      ) {
        categories.push({
          name: category,
          color: DOMAIN_COLORS[categories.length % DOMAIN_COLORS.length],
        })
      }
    })
  })

  return categories
}

const MatrixActivityList = ({ items, loadIndicators, selStandards }) => {
  if (!items || !items.length) return null

  const standard = (key) => {
    const groups = selStandards || []
    const hash = Object.assign({}, ...groups.map((group) => group.standards))
    return hash[key]
  }

  const itemStandard = (key, type, standardData) => (
    <li key={key}>
      <div className="main-item-standards">
        <div className="inner-item-standards">
          <span>
            {key}:{" "}
            {type === "student"
              ? standard(key) && standard(key).student_indicator_language
              : standard(key) && standard(key).adult_indicator_language}
          </span>
        </div>
        <div className="inner-list-of-item-standards">
          <li>{standardData.connection_explanation}</li>
        </div>
      </div>
    </li>
  )

  return (
    <div className="spr-matrix-section">
      <div className="main-program-matrix">
        {items.map((item, index) => {
          const selStandardsKeys = Object.keys(item.sel_standards || {})
          const categories = findLoadIndicatorsByKeys(
            selStandardsKeys,
            loadIndicators,
          )
          const backgroundColorClass = index % 2 === 0 ? "item-even" : "item-odd"
          const backgroundColorClassforGrades = index % 2 === 0 ? "item-odd" : "item-even"

          return (
            <div
              key={item.id || index}
              className={`report-container ${backgroundColorClass}`}
            >
              <div className="report-header">
                <div className="activity-name">
                  <span>{startCase(item.activity || "")}</span>
                </div>
                <div className="activity-description-parent-div">
                  <div className="activity-description">
                    <span>{item.description}</span>
                  </div>
                  <div className="report-categories">
                    {categories.map((category, categoryIndex) => (
                      <span
                        key={category.name}
                        style={{ color: category.color }}
                      >
                        {category.name}
                        {categoryIndex < categories.length - 1 && (
                          <span
                            style={{ marginLeft: "3px", marginRight: "3px" }}
                          >
                            |
                          </span>
                        )}
                        {(categoryIndex + 1) % 3 === 0
                          && categoryIndex < categories.length - 1 && <br />}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              {item.category !== "Adult SEL"
                && Array.isArray(item.internal_selected_grades)
                && item.internal_selected_grades.length > 0 && (
                  <div className="main-grade-level">
                    <div className="inner-grade-level">
                      <div className="grade-level-title">
                        {commonStrings.gradeLevelTitle}
                      </div>
                      <div className="grades">
                        <div className="individual-grades">
                          {item.internal_selected_grades.map(
                            (grade, gradeIndex) => grade
                              && grade.grade && (
                                <span
                                  key={gradeIndex}
                                  className={`grade-item ${backgroundColorClassforGrades}`}
                                >
                                  {grade.grade}
                                </span>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
              )}
              <div className="report-details">
                <div className="tier-domain-time">
                  <div className="report-section">
                    <div className="tier-type-title">
                      <span>{commonStrings.TierType}</span>
                    </div>
                    <div className="tier-of-support-type">
                      <span>{item.tier_of_support}</span>
                    </div>
                  </div>
                  <div className="report-section">
                    <div className="domain-title">
                      <span>{commonStrings.Domain}</span>
                    </div>
                    <div className="tier-of-support-type">
                      <span>{item.category}</span>
                    </div>
                  </div>
                  <div className="report-section">
                    <div className="length-of-time">
                      <span>{commonStrings.LengthofTime}</span>
                    </div>
                    <div className="tier-of-support-type">
                      <span>{item.length_of_time}</span>
                    </div>
                  </div>
                </div>
                <div className="report-section-data-collected">
                  <div className="data-collected">
                    <span>{commonStrings.DataCollected}</span>
                  </div>
                  <div className="tier-of-support-type">
                    <span
                      style={{
                        color: item.data_collection ? "#000000" : "#B9B9B9",
                      }}
                    >
                      {item.data_collection || "Data not yet collected."}
                    </span>
                  </div>
                </div>
              </div>
              <div className="activity-details-report-content">
                <div className="promotes-sel-body">
                  <div className="report-promotes-sel">
                    <span>How {item.activity} Promotes SEL</span>
                  </div>
                  <div className="sel-standards-body">
                    <ul>
                      {selStandardsKeys
                        .filter(
                          (key) => item.sel_standards[key].connection_type
                            === "promotes",
                        )
                        .map((key) => itemStandard(
                          key,
                          item.adult ? "adult" : "student",
                          item.sel_standards[key],
                        ))}
                    </ul>
                  </div>
                </div>
                <div className="report-reinforces">
                  <div className="report-sel-reinforces">
                    <span>How {item.activity} Reinforces SEL</span>
                  </div>
                  <div className="reinforces-list">
                    <ul>
                      {selStandardsKeys
                        .filter(
                          (key) => item.sel_standards[key].connection_type
                            === "reinforces",
                        )
                        .map((key) => itemStandard(
                          key,
                          item.adult ? "adult" : "student",
                          item.sel_standards[key],
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Reuses the real MTSS Report tab's own Mtss.jsx component as-is (it has no filters/PDF of its
// own to strip), fed by the same /program_matrix/program_matrix_items/mtss_report endpoint
// Reports.jsx/Mtss.jsx already call — this summary panel just wires up the same lifted state
// that component expects from its parent. The per-tier Student/Adult toggle state (`type`) is
// lifted further, into SchoolProgressReport, so the PDF download can follow the same selection.
const MtssActivityBreakdown = ({ type, setType }) => {
  const [mtssReport, setMtssReport] = useState([])
  const [conditionalMessage, setConditionalMessage] = useState([])
  const [tierPercentages, setTierPercentages] = useState([])

  useEffect(() => {
    APIClient.get(
      "/program_matrix/program_matrix_items/mtss_report",
      (response) => {
        setMtssReport(response)
      },
    )
  }, [])

  const updateTierData = () => {}

  const handleToggleChange = (tierIndex, newType) => {
    setType((prevTypes) => {
      const updatedTypes = [...prevTypes]
      updatedTypes[tierIndex] = newType || "student"
      return updatedTypes
    })
  }

  if (!mtssReport || !mtssReport.activity_breakdown_by_tier) return null

  return (
    <Mtss
      Mtssreport={mtssReport}
      setConditionalMessage={setConditionalMessage}
      conditionalMessage={conditionalMessage}
      tierPercentages={tierPercentages}
      setTierPercentages={setTierPercentages}
      updateTierData={updateTierData}
      type={type}
      setType={setType}
      handleToggleChange={handleToggleChange}
    />
  )
}

// The per-section PDF download shown top-right inside an opened report panel.
const SectionDownload = ({
  section, title, href, schoolName, implementation, programMatrix, mtssTypes, selQualityToggle,
}) => (
  <div className="spr-section-download">
    <SchoolProgressReportButton
      section={section}
      fileName={`${schoolName} ${title}.pdf`}
      href={href}
      implementation={implementation}
      programMatrix={programMatrix}
      mtssTypes={mtssTypes}
      selQualityToggle={selQualityToggle}
    />
  </div>
)

const ProgramMatrixDetail = ({
  programMatrix, mtssTypes, setMtssTypes, selQualityToggle, setSelQualityToggle, downloadPdfUrl, schoolName,
}) => {
  const download = (section, title) => (
    <SectionDownload
      section={section}
      title={title}
      href={downloadPdfUrl}
      schoolName={schoolName}
      programMatrix={programMatrix}
      mtssTypes={mtssTypes}
      selQualityToggle={selQualityToggle}
    />
  )

  const [sortedPyramidData, setSortedPyramidData] = useState([])

  // ProgramMatrix/SELQuality.jsx (and SELSchoolCapacity.jsx, which it renders) read these
  // straight off window.gon — the same globals the real Program Matrix page sets via
  // ProgramMatricesController#program_matrix_settings — rather than taking them as props.
  // SELSchoolCapacity's makeDessaArray(window.gon.SEL_STANDARDS) runs synchronously inside a
  // useState initializer on first render, so these have to be set here synchronously during
  // render (not in an effect), before that component mounts.
  window.gon = window.gon || {}
  window.gon.CURRENT_SCHOOL_ACTIVITIES = programMatrix.current_school_activities || []
  window.gon.SEL_STANDARDS = programMatrix.sel_standards || []
  window.gon.DESS_SEL_STANDARDS = programMatrix.dessa_standards || []

  return (
    <div>
      <Collapse
        bordered={false}
        className="spr-matrix-reports"
        style={{ backgroundColor: "white" }}
      >
        <Panel
          key="selScan"
          header={<div className="spr-matrix-reports__title">SEL Scan</div>}
        >
          {download("sel_scan", "SEL Scan")}
          <SelDomainBands
            groups={programMatrix.sel_standards}
            completedItems={programMatrix.completed_items}
            showDescription
          />
        </Panel>
        <Panel
          key="dessa"
          header={<div className="spr-matrix-reports__title">DESSA Report</div>}
        >
          {download("dessa", "DESSA Report")}
          <SelDomainBands
            groups={programMatrix.dessa_standards}
            completedItems={programMatrix.completed_items}
            showDescription={false}
          />
        </Panel>
        <Panel
          key="selQuality"
          header={<div className="spr-matrix-reports__title">SEL Quality</div>}
        >
          {download("sel_quality", "SEL Quality")}
          <div className="spr-matrix-section">
            <ProgramMatrixSELQuality
              matrix={programMatrix}
              currentSchool={null}
              onTypeChange={setSelQualityToggle}
            />
          </div>
        </Panel>
        <Panel
          key="activityDetails"
          header={
            <div className="spr-matrix-reports__title">Activity Details</div>
          }
        >
          {download("activity_details", "Activity Details")}
          <MatrixActivityList
            items={programMatrix.completed_items}
            loadIndicators={programMatrix.load_indicators}
            selStandards={programMatrix.sel_standards}
          />
        </Panel>
        <Panel
          key="mtss"
          header={<div className="spr-matrix-reports__title">MTSS Report</div>}
        >
          {download("mtss", "MTSS Report")}
          <div className="spr-matrix-section">
            <div className="spr-matrix-row">
              <div className="spr-matrix-pyramid">
                <Pyramid
                  Mtssreport={{ pyramid_Hash: programMatrix.mtss_tiers || [] }}
                  setSortedPyramidData={setSortedPyramidData}
                  sortedPyramidData={sortedPyramidData}
                />
              </div>
            </div>
            <MtssActivityBreakdown type={mtssTypes} setType={setMtssTypes} />
          </div>
        </Panel>
      </Collapse>
    </div>
  )
}

const SchoolProgressReport = ({ dataUrl, downloadPdfUrl, schoolName }) => {
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  // Per-tier Student/Adult selection of the MTSS "SEL Indicators being Promoted and Reinforced"
  // toggles; anything but "adult" means Student, same as processTierData.
  const [mtssTypes, setMtssTypes] = useState([])
  // The SEL Quality panel's Student/Adult toggle; null until that panel is first opened.
  const [selQualityToggle, setSelQualityToggle] = useState(null)

  useEffect(() => {
    fetch(dataUrl, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }
        return response.json()
      })
      .then(setData)
      .catch(() => setError(true))
  }, [dataUrl])

  if (error) {
    return (
      <div className="spr-state-error">
        Couldn&apos;t load the progress report. Please refresh the page.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="spr-state-loading">
        <Loader />
      </div>
    )
  }

  const { implementation, sustainability, programMatrix } = {
    implementation: data.implementation,
    sustainability: data.sustainability,
    programMatrix: data.program_matrix,
  }

  return (
    <div className="spr-page">
      <div className="spr-page__header">
        <div>
          <div className="spr-page__title">School Progress Report</div>
          <div className="spr-page__subtitle">
            {schoolName} &middot;{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
            })}
          </div>
        </div>
        <SchoolProgressReportButton
          href={downloadPdfUrl}
          implementation={implementation}
          programMatrix={programMatrix}
          mtssTypes={mtssTypes}
          selQualityToggle={selQualityToggle}
        />
      </div>

      <div className="spr-hero">
        <div className="spr-hero__overall">
          <ProgressRing
            percent={data.overall_percent}
            radius={35}
            strokeWidth={9}
          />
          <div>
            <div className="spr-hero__overall-label">Overall</div>
            <div className="spr-hero__overall-caption">across 3 sections</div>
          </div>
        </div>
        <div className="spr-hero__stats">
          <div className="spr-hero__stat">
            <div className="spr-hero__stat-value spr-hero__stat-value--implementation">
              {implementation.present
                ? `${implementation.overall_percent}%`
                : "—"}
            </div>
            <div className="spr-hero__stat-caption">Implementation</div>
          </div>
          <div className="spr-hero__stat">
            <div className="spr-hero__stat-value spr-hero__stat-value--sustainability">
              {sustainability.present ? sustainability.rating : "—"}
            </div>
            <div className="spr-hero__stat-caption">Sustainability</div>
          </div>
          <div className="spr-hero__stat">
            <div className="spr-hero__stat-value spr-hero__stat-value--program-matrix">
              {programMatrix.present ? programMatrix.activities_count : "—"}
            </div>
            <div className="spr-hero__stat-caption">
              Matrix activities logged
            </div>
          </div>
        </div>
      </div>

      <div className="spr-panel-card">
        <Collapse
          bordered={false}
          style={{ background: "transparent" }}
          className="school-progress-report-collapse"
        >
          <Panel
            key="implementation"
            style={{ border: "none" }}
            header={(
              <SectionHeader
                icon={(
                  <svg
                    width={18}
                    height={18}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={COLORS.implementation}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 11l3 3L22 4" />
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                  </svg>
                )}
                iconTint="#FBF0DA"
                title="Implementation Report"
                badgeColor="#8A6414"
                badgeTint="#FBF0DA"
                badge={
                  implementation.present
                    ? `${implementation.overall_percent}% overall`
                    : "Not submitted"
                }
              />
            )}
          >
            {implementation.present ? (
              <>
                <SectionDownload
                  section="implementation"
                  title="Implementation Report"
                  href={downloadPdfUrl}
                  schoolName={schoolName}
                  implementation={implementation}
                />
                <ImplementationDetail implementation={implementation} />
              </>
            ) : (
              <div className="spr-empty-section">
                No Implementation Report has been submitted for this school yet.
                Please submit an Implementation Report by going to the
                Implementation Rubric to see this section.
              </div>
            )}
          </Panel>

          <Panel
            key="sustainability"
            style={{ border: "none" }}
            header={(
              <SectionHeader
                icon={(
                  <svg
                    width={18}
                    height={18}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={COLORS.sustainability}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 2c4 3 7 6.5 7 11a7 7 0 01-14 0c0-4.5 3-8 7-11z" />
                  </svg>
                )}
                iconTint="#E3E8EE"
                title="Sustainability Report"
                meta={
                  sustainability.present
                    ? `${sustainability.areas_graded_count} of ${sustainability.areas_total_count} areas graded`
                    : null
                }
                badgeColor="#33485D"
                badgeTint="#E3E8EE"
                badge={
                  sustainability.present
                    ? sustainability.rating
                    : "Not submitted"
                }
              />
            )}
          >
            {sustainability.present ? (
              <>
                <SectionDownload
                  section="sustainability"
                  title="Sustainability Report"
                  href={downloadPdfUrl}
                  schoolName={schoolName}
                />
                <SustainabilityDetail sustainability={sustainability} />
              </>
            ) : (
              <div className="spr-empty-section">
                No Sustainability Report has been submitted for this school yet.
                Please submit a Sustainability Report by going to the
                Sustainability Rubric to see this section.
              </div>
            )}
          </Panel>

          <Panel
            key="programMatrix"
            style={{ border: "none" }}
            header={(
              <SectionHeader
                icon={(
                  <svg
                    width={18}
                    height={18}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={COLORS.programMatrix}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="8" height="8" rx="1.5" />
                    <rect x="13" y="3" width="8" height="8" rx="1.5" />
                    <rect x="3" y="13" width="8" height="8" rx="1.5" />
                    <rect x="13" y="13" width="8" height="8" rx="1.5" />
                  </svg>
                )}
                iconTint="#DAF1F1"
                title="Program Matrix"
                meta="SEL Scan · DESSA · SEL Quality · Activity Details · MTSS"
                badgeColor="#0B7A7B"
                badgeTint="#DAF1F1"
                badge={
                  programMatrix.present
                    ? `${programMatrix.activities_count} activities`
                    : "Not submitted"
                }
              />
            )}
          >
            {programMatrix.present ? (
              <ProgramMatrixDetail
                programMatrix={programMatrix}
                mtssTypes={mtssTypes}
                setMtssTypes={setMtssTypes}
                selQualityToggle={selQualityToggle}
                setSelQualityToggle={setSelQualityToggle}
                downloadPdfUrl={downloadPdfUrl}
                schoolName={schoolName}
              />
            ) : (
              <div className="spr-empty-section">
                No Program Matrix has been submitted for this school yet.
              </div>
            )}
          </Panel>
        </Collapse>
      </div>
    </div>
  )
}

export default SchoolProgressReport
